/**
 * The DOCE invoice-reconciliation report: its columns, and what it looks like on paper.
 *
 * Built in the browser from the rows the API returns, like the DOCE customer report and the order
 * receipt, so the exports are three local renders of one fetch rather than three passes over the table.
 */

import type { Column } from './csv';

export interface DoceTxnRow {
  transactionId: string;
  tcn: string;
  createdOn: string;
  accountId: string;
  accountName: string;
  submitterId: string;
  rateLabel: string | null;
  firstName: string;
  lastName: string;
  state: string;
  rateCents: number;
  billable: boolean;
  excludedReason: string | null;
  attempts: number;
  correctionOfTcn: string | null;
  correctedByTcn: string | null;
  notSentReason: string | null;
  /** When the customer removed it from their list. Still billable — the channeler had already run it. */
  deletedOn: string | null;
}

export interface SubmitterTotal {
  submitterId: string;
  label: string;
  accountNames: string[];
  rateCents: number;
  billable: number;
  excluded: number;
  extraAttempts: number;
  amountCents: number;
}

export interface DoceTxnReport {
  generatedOn: string;
  from: string;
  to: string;
  rows: DoceTxnRow[];
  billing: {
    bySubmitter: SubmitterTotal[];
    totalBillable: number;
    totalExcluded: number;
    totalExtraAttempts: number;
    totalCents: number;
  };
  accountsSearched: number;
}

const esc = (s: string): string =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const money = (cents: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

/** `2026-08-14` — the submission date, which is what an invoice line is dated by. */
export const shortDate = (iso: string): string => (iso || '').slice(0, 10);

/** The subject, or a dash. Names are the only way a human spot-checks a line against a paper file. */
export const subjectName = (r: DoceTxnRow): string =>
  [r.lastName, r.firstName].filter(Boolean).join(', ') || '—';

/**
 * Why this row is or is not on the invoice, in one short phrase.
 *
 * The excluded reason when there is one, otherwise a note only when something about the row is unusual.
 * A billable, first-attempt, ordinary submission gets nothing — a column of "OK" tells nobody anything.
 */
export function rowNote(r: DoceTxnRow): string {
  if (r.excludedReason) return r.excludedReason;
  const notes: string[] = [];
  if (r.attempts > 1) notes.push(`sent ${r.attempts} times (same TCN)`);
  if (r.correctedByTcn) notes.push(`superseded by ${r.correctedByTcn}`);
  // Worth stating: it is charged, and it is not in the customer's own list, so the two will not tally.
  if (r.deletedOn) notes.push(`deleted by the customer ${shortDate(r.deletedOn)} — still charged`);
  return notes.join('; ');
}

export const DOCE_TXN_COLUMNS: readonly Column<DoceTxnRow>[] = [
  { header: 'Date', value: (r) => shortDate(r.createdOn) },
  { header: 'Customer', value: (r) => r.accountName },
  { header: 'Submitter ID', value: (r) => r.submitterId },
  { header: 'TCN', value: (r) => r.tcn },
  { header: 'Subject', value: (r) => subjectName(r) },
  { header: 'State', value: (r) => r.state },
  // Yes/No rather than TRUE/FALSE: this is read by a person against a paper invoice.
  { header: 'Billable', value: (r) => (r.billable ? 'Yes' : 'No') },
  { header: 'Rate', value: (r) => money(r.rateCents) },
  // Zero, not blank, for an excluded row — a blank reads as missing data rather than as nothing owed.
  { header: 'Charge', value: (r) => money(r.billable ? r.rateCents : 0) },
  { header: 'Sends', value: (r) => String(r.attempts) },
  { header: 'Note', value: (r) => rowNote(r) },
];

/** `EFTCreator-DOCE-transactions-20260801-20260831`. */
export function txnReportFilename(from: string, to: string): string {
  const flat = (d: string) => d.replace(/-/g, '');
  return `EFTCreator-DOCE-transactions-${flat(from)}-${flat(to)}`;
}

/** The month as a person writes it, for the top of the printed page. */
export function rangeLabel(from: string, to: string): string {
  const fmt = (d: string) => {
    const at = new Date(`${d}T12:00:00Z`);
    return Number.isNaN(at.getTime())
      ? d
      : at.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
  };
  return `${fmt(from)} — ${fmt(to)}`;
}

/**
 * The printable document.
 *
 * Totals FIRST, then the detail. This is read beside an invoice: the question is "does the total match",
 * and the rows exist to explain the answer rather than to be read in order.
 */
export function doceTxnReportHtml(report: DoceTxnReport): string {
  const { rows, billing } = report;

  const lines = billing.bySubmitter
    .map(
      (l) => `<tr>
      <td><div class="cust">${esc(l.label)}</div><div class="sub mono">${esc(l.submitterId)}</div></td>
      <td class="num">${l.billable}</td>
      <td class="num">${esc(money(l.rateCents))}</td>
      <td class="num strong">${esc(money(l.amountCents))}</td>
      <td class="num">${l.excluded || '<span class="none">—</span>'}</td>
    </tr>`,
    )
    .join('');

  const detail = rows
    .map(
      (r) => `<tr${r.billable ? '' : ' class="excluded"'}>
      <td>${esc(shortDate(r.createdOn))}</td>
      <td>${esc(r.accountName)}</td>
      <td class="mono small">${esc(r.tcn)}</td>
      <td>${esc(subjectName(r))}</td>
      <td>${esc(r.state)}</td>
      <td class="num">${r.attempts > 1 ? r.attempts : ''}</td>
      <td class="num">${esc(money(r.billable ? r.rateCents : 0))}</td>
      <td class="sub">${esc(rowNote(r))}</td>
    </tr>`,
    )
    .join('');

  return `<!doctype html><html><head><meta charset="utf-8">
<title>${esc(txnReportFilename(report.from, report.to))}</title>
<style>
  @page { size: letter landscape; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #222; font-size: 10pt; margin: 0; padding: 12mm; }
  .head { border-bottom: 3px solid #55B848; padding-bottom: 10px; margin-bottom: 16px;
          display: flex; justify-content: space-between; align-items: flex-end; }
  .brand { font-size: 18pt; font-weight: bold; letter-spacing: -0.5px; }
  .brand span { color: #55B848; }
  .title { font-size: 12pt; margin-top: 2px; }
  .meta { text-align: right; font-size: 9pt; color: #777; line-height: 1.5; }
  h2 { font-size: 9.5pt; text-transform: uppercase; letter-spacing: .6px; color: #777; margin: 22px 0 6px; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: 8pt; text-transform: uppercase; letter-spacing: .5px; color: #777;
       border-bottom: 1.5px solid #ddd; padding: 5px 7px; }
  th.num, td.num { text-align: right; }
  td { padding: 5px 7px; border-bottom: 1px solid #eee; vertical-align: top; }
  .cust { font-weight: bold; }
  .sub { font-size: 8pt; color: #888; }
  .small { font-size: 7.5pt; }
  .mono { font-family: monospace; }
  .strong { font-weight: bold; }
  .none { color: #bbb; }
  /* An excluded row is still shown — it is the evidence for the number being disputed. */
  .excluded td { color: #999; }
  .total { border-top: 2px solid #333; font-weight: bold; }
  .total td { padding-top: 7px; }
  tr { break-inside: avoid; }
  .note { font-size: 8.5pt; color: #777; margin: 8px 0 0; max-width: 110ch; }
  .foot { margin-top: 18px; padding-top: 8px; border-top: 1px solid #eee; font-size: 8pt; color: #999; }
</style></head><body>
  <div class="head">
    <div>
      <div class="brand">EFT<span>Creator</span></div>
      <div class="title">FBI channeler reconciliation — DOCE submissions</div>
      <div class="sub">${esc(rangeLabel(report.from, report.to))}</div>
    </div>
    <div class="meta">
      ${rows.length} submission${rows.length === 1 ? '' : 's'}<br>
      ${billing.totalBillable} billable, ${billing.totalExcluded} excluded<br>
      <strong>${esc(money(billing.totalCents))}</strong> expected
    </div>
  </div>

  <h2>Expected invoice</h2>
  <table>
    <thead><tr>
      <th>Submitter</th><th class="num">Billable</th><th class="num">Rate</th>
      <th class="num">Amount</th><th class="num">Excluded</th>
    </tr></thead>
    <tbody>
      ${lines || '<tr><td colspan="5">No DOCE submissions in this range.</td></tr>'}
      <tr class="total">
        <td>Total</td>
        <td class="num">${billing.totalBillable}</td>
        <td></td>
        <td class="num">${esc(money(billing.totalCents))}</td>
        <td class="num">${billing.totalExcluded}</td>
      </tr>
    </tbody>
  </table>
  <p class="note">
    A resubmission is the same TCN sent again and is never a second line. A correction is a new TCN
    replacing one that errored, and is excluded because the submission it replaces was already charged.
    A submission the router never sent is excluded because nobody processed it.
  </p>

  <h2>Submissions</h2>
  <table>
    <thead><tr>
      <th>Date</th><th>Customer</th><th>TCN</th><th>Subject</th><th>State</th>
      <th class="num">Sends</th><th class="num">Charge</th><th>Note</th>
    </tr></thead>
    <tbody>${detail || '<tr><td colspan="8">Nothing in this range.</td></tr>'}</tbody>
  </table>

  <div class="foot">
    Read from the EFTCreator transaction record across ${report.accountsSearched} DOCE-enabled accounts.
    Lakota Software Solutions, Inc. — internal.
  </div>
</body></html>`;
}
